import React, { useState, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { SelectButton } from 'primereact/selectbutton';
import { Divider } from 'primereact/divider';
import { Card } from 'primereact/card';
import { Password } from 'primereact/password';
import { InputTextarea } from 'primereact/inputtextarea';
import axios from 'axios';
import { toast } from 'react-toastify';

const DEFAULT_SETTINGS = {
  // Email / SMTP
  emailServer: '',
  emailPort: '465',
  emailUser: '',
  emailPassword: '',
  emailFromName: '',
  emailFromAddress: '',
  // Platform
  siteName: 'Schoolwise',
  siteTagline: 'Empowering Education Through Technology',
  supportEmail: '',
  allowSchoolRegistration: 'true',
  // Security
  enforceEmailVerification: 'false',
  sessionDurationHours: '24',
  minPasswordLength: '6',
  maxLoginAttempts: '5',
  // Maintenance
  maintenanceMode: 'false',
  maintenanceMessage: 'We are performing scheduled maintenance. Please check back later.',
};

const BOOL_OPTIONS = [
  { label: 'Enabled', value: 'true' },
  { label: 'Disabled', value: 'false' },
];

const PlatformSettings = () => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  useEffect(() => {
    axios.get('/api/settings')
      .then(res => setSettings(prev => ({ ...prev, ...res.data })))
      .catch(err => console.error('Failed to load settings:', err));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleBoolChange = (name, value) => {
    if (value !== null) setSettings(prev => ({ ...prev, [name]: value }));
  };

  const saveKeys = async (keys) => {
    setSaving(true);
    const payload = Object.fromEntries(keys.map(k => [k, settings[k]]));
    try {
      const res = await axios.post('/api/settings', payload);
      if (res.data.success) {
        toast.success('Settings saved', { autoClose: 2000 });
      } else {
        toast.error(res.data.msg || 'Save failed', { autoClose: 3000 });
      }
    } catch {
      toast.error('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const testEmailConnection = async () => {
    setTestingEmail(true);
    try {
      const res = await axios.post('/api/settings/testEmail', {
        emailServer: settings.emailServer,
        emailPort: settings.emailPort,
        emailUser: settings.emailUser,
        emailPassword: settings.emailPassword,
      });
      if (res.data.success) toast.success(res.data.msg, { autoClose: 2000 });
      else toast.error(res.data.msg, { autoClose: 3000 });
    } catch {
      toast.error('Connection test failed');
    } finally {
      setTestingEmail(false);
    }
  };

  return (
    <div className="p-4">
      <div className="mb-4">
        <h2 className="text-2xl font-bold m-0">Platform Settings</h2>
        <p className="text-color-secondary mt-1">Configure system-wide settings for your platform</p>
      </div>

      <TabView>
        {/* ── Email / SMTP ── */}
        <TabPanel header="Email / SMTP" leftIcon="pi pi-envelope mr-2">
          <Card>
            <div className="grid formgrid">
              <div className="field col-12 md:col-6">
                <label htmlFor="emailServer">SMTP Server</label>
                <InputText id="emailServer" name="emailServer" value={settings.emailServer} onChange={handleChange} className="w-full" placeholder="smtp.gmail.com" />
              </div>
              <div className="field col-12 md:col-6">
                <label htmlFor="emailPort">SMTP Port</label>
                <InputText id="emailPort" name="emailPort" value={settings.emailPort} onChange={handleChange} className="w-full" placeholder="465" />
              </div>
              <div className="field col-12 md:col-6">
                <label htmlFor="emailUser">SMTP Username</label>
                <InputText id="emailUser" name="emailUser" value={settings.emailUser} onChange={handleChange} className="w-full" placeholder="your@email.com" />
              </div>
              <div className="field col-12 md:col-6">
                <label htmlFor="emailPassword">SMTP Password</label>
                <Password id="emailPassword" name="emailPassword" value={settings.emailPassword} onChange={handleChange} feedback={false} toggleMask style={{ width: '100%' }} inputStyle={{ width: '100%' }} />
              </div>
              <div className="field col-12 md:col-6">
                <label htmlFor="emailFromName">From Name</label>
                <InputText id="emailFromName" name="emailFromName" value={settings.emailFromName} onChange={handleChange} className="w-full" placeholder="School Management" />
              </div>
              <div className="field col-12 md:col-6">
                <label htmlFor="emailFromAddress">From Address</label>
                <InputText id="emailFromAddress" name="emailFromAddress" value={settings.emailFromAddress} onChange={handleChange} className="w-full" placeholder="noreply@yourdomain.com" />
              </div>
            </div>
            <Divider />
            <div className="flex gap-2 flex-wrap">
              <Button label="Test Connection" icon="pi pi-send" className="p-button-secondary" loading={testingEmail} onClick={testEmailConnection} />
              <Button label="Save Email Settings" icon="pi pi-check" loading={saving} onClick={() => saveKeys(['emailServer', 'emailPort', 'emailUser', 'emailPassword', 'emailFromName', 'emailFromAddress'])} />
            </div>
          </Card>
        </TabPanel>

        {/* ── Platform Info ── */}
        <TabPanel header="Platform" leftIcon="pi pi-globe mr-2">
          <Card>
            <div className="grid formgrid">
              <div className="field col-12 md:col-6">
                <label htmlFor="siteName">Platform Name</label>
                <InputText id="siteName" name="siteName" value={settings.siteName} onChange={handleChange} className="w-full" />
              </div>
              <div className="field col-12 md:col-6">
                <label htmlFor="siteTagline">Tagline</label>
                <InputText id="siteTagline" name="siteTagline" value={settings.siteTagline} onChange={handleChange} className="w-full" />
              </div>
              <div className="field col-12 md:col-6">
                <label htmlFor="supportEmail">Support Email</label>
                <InputText id="supportEmail" name="supportEmail" value={settings.supportEmail} onChange={handleChange} className="w-full" placeholder="support@yourdomain.com" />
              </div>
              <div className="field col-12">
                <label className="block mb-2">Allow Public School Registration</label>
                <SelectButton value={settings.allowSchoolRegistration} onChange={e => handleBoolChange('allowSchoolRegistration', e.value)} options={BOOL_OPTIONS} />
                <small className="text-color-secondary block mt-1">When disabled, only platform admins can register new schools from the dashboard.</small>
              </div>
            </div>
            <Divider />
            <Button label="Save Platform Settings" icon="pi pi-check" loading={saving} onClick={() => saveKeys(['siteName', 'siteTagline', 'supportEmail', 'allowSchoolRegistration'])} />
          </Card>
        </TabPanel>

        {/* ── Security ── */}
        <TabPanel header="Security" leftIcon="pi pi-shield mr-2">
          <Card>
            <div className="grid formgrid">
              <div className="field col-12">
                <label className="block mb-2">Enforce Email Verification</label>
                <SelectButton value={settings.enforceEmailVerification} onChange={e => handleBoolChange('enforceEmailVerification', e.value)} options={BOOL_OPTIONS} />
                <small className="text-color-secondary block mt-1">Users must verify their email before they can log in.</small>
              </div>
              <div className="field col-12 md:col-4">
                <label htmlFor="sessionDurationHours">Session Duration (hours)</label>
                <InputText id="sessionDurationHours" name="sessionDurationHours" value={settings.sessionDurationHours} onChange={handleChange} className="w-full" placeholder="24" />
              </div>
              <div className="field col-12 md:col-4">
                <label htmlFor="minPasswordLength">Min Password Length</label>
                <InputText id="minPasswordLength" name="minPasswordLength" value={settings.minPasswordLength} onChange={handleChange} className="w-full" placeholder="6" />
              </div>
              <div className="field col-12 md:col-4">
                <label htmlFor="maxLoginAttempts">Max Login Attempts</label>
                <InputText id="maxLoginAttempts" name="maxLoginAttempts" value={settings.maxLoginAttempts} onChange={handleChange} className="w-full" placeholder="5" />
              </div>
            </div>
            <Divider />
            <Button label="Save Security Settings" icon="pi pi-check" loading={saving} onClick={() => saveKeys(['enforceEmailVerification', 'sessionDurationHours', 'minPasswordLength', 'maxLoginAttempts'])} />
          </Card>
        </TabPanel>

        {/* ── Maintenance ── */}
        <TabPanel header="Maintenance" leftIcon="pi pi-wrench mr-2">
          <Card>
            <div className="grid formgrid">
              <div className="field col-12">
                <label className="block mb-2">Maintenance Mode</label>
                <SelectButton value={settings.maintenanceMode} onChange={e => handleBoolChange('maintenanceMode', e.value)} options={BOOL_OPTIONS} />
                <small className="text-color-secondary block mt-1">When enabled, users see a maintenance page instead of the app.</small>
              </div>
              <div className="field col-12">
                <label htmlFor="maintenanceMessage">Maintenance Message</label>
                <InputTextarea id="maintenanceMessage" name="maintenanceMessage" value={settings.maintenanceMessage} onChange={handleChange} className="w-full" rows={3} />
              </div>
            </div>
            <Divider />
            <Button label="Save Maintenance Settings" icon="pi pi-check" loading={saving} onClick={() => saveKeys(['maintenanceMode', 'maintenanceMessage'])} />
          </Card>
        </TabPanel>
      </TabView>
    </div>
  );
};

export default PlatformSettings;
